/* eslint-disable no-param-reassign -- https://redux-toolkit.js.org/usage/immer-reducers  */
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { z } from 'zod';
import { draftClaimLineItemSchema } from '~/utils/schemas';
import {
  completeDraftClaimLineItem,
  invalidateLineItem,
} from './sharedActions';

export const draftClaimLineItemStateSchema = z.nullable(
  draftClaimLineItemSchema,
);

type DraftClaimLineItemState = z.infer<typeof draftClaimLineItemStateSchema>;

function assertInitialized(
  state: Draft<DraftClaimLineItemState>,
): asserts state is Draft<NonNullable<DraftClaimLineItemState>> {
  if (state === null) {
    throw new Error('Draft claim line item not initialized');
  }
}

export const draftClaimLineItemSlice = createSlice({
  name: 'draftClaimLineItem',
  initialState: null as DraftClaimLineItemState,
  reducers: {
    startNewClaim: (
      state,
      action: PayloadAction<z.infer<typeof draftClaimLineItemSchema>>,
    ) => action.payload,
    setQuantity: (
      state,
      action: PayloadAction<NonNullable<DraftClaimLineItemState>['quantity']>,
    ) => {
      assertInitialized(state);

      state.quantity = action.payload;
    },
    setReason: (
      state,
      action: PayloadAction<
        Required<
          Pick<
            NonNullable<DraftClaimLineItemState>,
            'reasonId' | 'reasonCategoryCode'
          >
        >
      >,
    ) => {
      assertInitialized(state);

      const { reasonId, reasonCategoryCode } = action.payload;

      if (reasonId !== state.reasonId) {
        state.reasonDetailId = undefined; // clear out any previous reason detail
      }

      state.reasonId = reasonId;
      state.reasonCategoryCode = reasonCategoryCode;
    },
    setReasonDetail: (
      state,
      action: PayloadAction<
        NonNullable<NonNullable<DraftClaimLineItemState>['reasonDetailId']>
      >,
    ) => {
      assertInitialized(state);

      state.reasonDetailId = action.payload;
    },
    setComments: (
      state,
      action: PayloadAction<NonNullable<DraftClaimLineItemState>['comments']>,
    ) => {
      assertInitialized(state);
      state.comments = action.payload;
    },
    setMeta: (
      state,
      action: PayloadAction<
        Pick<
          NonNullable<DraftClaimLineItemState>,
          'assets' | 'comments' | 'customFields'
        >
      >,
    ) => {
      assertInitialized(state);

      state.assets = action.payload.assets;
      state.comments = action.payload.comments;
      state.customFields = action.payload.customFields;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(invalidateLineItem, () => null)
      .addCase(completeDraftClaimLineItem, () => null);
  },
});
